import pluralize from 'pluralize';

import { type PlanPricing, type SubscriptionPlan } from '@air/api/types';
import { formatBytes } from '@air/utilities';

import { formatCreditCount, formatPotentialCreditUtilization } from '~/utils/creditUtils';

type GetPlanFeaturesArgs = { planId: SubscriptionPlan; context?: 'upgrade' } & Pick<
  PlanPricing,
  'pricingModel' | 'limits' | 'creditsPerCycle'
> & {
    includedStorage: PlanPricing['includedStorage'] | null;
  };

const getStorageFeature = ({ limits, includedStorage }: Pick<GetPlanFeaturesArgs, 'limits' | 'includedStorage'>) => {
  const planStorage = limits.storage ?? includedStorage;

  return planStorage ? `${formatBytes(planStorage)} storage` : 'Unlimited storage';
};

// Q3 '26 published enterprise allotment. The plans list and subscription do not return this yet.
const ENTERPRISE_STORAGE_FEATURE = '5+ TB storage';

export const getPlanFeatures = ({
  planId,
  context,
  pricingModel,
  limits,
  includedStorage,
  creditsPerCycle,
}: GetPlanFeaturesArgs): string[] | undefined => {
  const storageOptionItem = getStorageFeature({ limits, includedStorage });

  let membersOptionItem: string | null = null;

  if (pricingModel === 'credit' || limits.members === null) {
    membersOptionItem = 'Unlimited users';
  } else if (limits.members) {
    membersOptionItem = `${pluralize('user', limits.members, true)}`;
  }

  const potentialCreditUtilization = creditsPerCycle ? formatPotentialCreditUtilization(creditsPerCycle) : undefined;

  switch (planId) {
    case 'basic': {
      if (pricingModel !== 'credit') {
        return [...(membersOptionItem ? [membersOptionItem] : []), storageOptionItem, '7 day deletion recovery'];
      }

      return [...(potentialCreditUtilization ? [potentialCreditUtilization] : []), '7 day deletion recovery'];
    }

    case 'creator':
      return [...(membersOptionItem ? [membersOptionItem] : []), storageOptionItem, '30 day deletion recovery'];

    case 'starter': {
      if (pricingModel !== 'credit') {
        return [...(membersOptionItem ? [membersOptionItem] : []), storageOptionItem, '30 day deletion recovery'];
      }

      return [
        ...(potentialCreditUtilization ? [potentialCreditUtilization] : []),
        'Unlimited users',
        '30 day deletion recovery',
      ];
    }

    case 'plus':
      return [...(membersOptionItem ? [membersOptionItem] : []), storageOptionItem, '30 day deletion recovery window'];

    case 'pro':
      return [...(membersOptionItem ? [membersOptionItem] : []), storageOptionItem, '90 day deletion recovery'];

    case 'business': {
      if (pricingModel === 'fixed' && context === 'upgrade') {
        return [storageOptionItem, 'Share link permissions', 'Public API & MCP', '90 day deletion recovery'];
      }

      if (pricingModel !== 'credit') {
        return [...(membersOptionItem ? [membersOptionItem] : []), storageOptionItem, '90 day deletion recovery'];
      }

      return [
        ...(potentialCreditUtilization ? [potentialCreditUtilization] : []),
        'Share link permissions',
        'Public API & MCP',
        '90 day deletion recovery',
      ];
    }

    case 'enterprise':
      return [
        (limits.storage ?? includedStorage) ? storageOptionItem : ENTERPRISE_STORAGE_FEATURE,
        'Unlimited users with custom roles & permissions',
        'SAML-based SSO',
        'Reporting, analytics, API Access & custom integrations',
        'Dedicated onboarding, support Slack, and CSM',
      ];
  }
};

/**
 * Fixed plans list their monthly credits as a regular feature right after storage, instead of the
 * highlighted standalone line the credit and per-member plans use.
 */
export const getPlanFeaturesWithInlineCredits = (args: GetPlanFeaturesArgs): string[] => {
  const features = getPlanFeatures(args) ?? [];

  if (args.pricingModel !== 'fixed' || !args.creditsPerCycle) {
    return features;
  }

  const creditsFeature = `${formatCreditCount(args.creditsPerCycle)} credits every month`;
  const storageFeature = getStorageFeature(args);

  if (!features.includes(storageFeature)) {
    return [creditsFeature, ...features];
  }

  return [storageFeature, creditsFeature, ...features.filter((feature) => feature !== storageFeature)];
};
